import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const PERIODS = [
  { value: 'today', label: "Aujourd'hui" },
  { value: 'week', label: 'Cette semaine' },
  { value: 'month', label: 'Ce mois' },
  { value: 'year', label: 'Cette annee' },
  { value: 'custom', label: 'Periode personnalisee' },
  { value: 'all', label: 'Tout' }
];

const STATUSES = [
  { value: '', label: 'Tous les statuts' },
  { value: 'en_retard', label: 'En retard' },
  { value: 'a_venir', label: 'A venir' },
  { value: 'payee', label: 'Payee' },
  { value: 'partiellement_payee', label: 'Partiellement payee' },
  { value: 'non_payee', label: 'Non payee' },
  { value: 'non_configure', label: 'Non configure' },
  { value: 'valide', label: 'Document valide' },
  { value: 'brouillon', label: 'Document brouillon' },
  { value: 'en_attente', label: 'Document en attente' }
];

const METHODS = [
  { value: '', label: 'Toutes les methodes' },
  { value: 'traite', label: 'Traite' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'especes', label: 'Especes' },
  { value: 'virement', label: 'Virement' },
  { value: 'versement_bancaire', label: 'Versement bancaire' },
  { value: 'comptant', label: 'Comptant' },
  { value: 'autre', label: 'Autre' }
];

const SECTIONS = [
  { key: 'toutesFactures', label: 'Toutes les factures', icon: 'fa-file-invoice' },
  { key: 'echeancesRetard', label: 'Echeances en retard', icon: 'fa-triangle-exclamation' },
  { key: 'echeancesAVenir', label: 'Echeances a venir', icon: 'fa-calendar-days' },
  { key: 'echeancesPayees', label: 'Echeances payees', icon: 'fa-circle-check' },
  { key: 'facturesPartiellementPayees', label: 'Partiellement payees', icon: 'fa-scale-unbalanced' },
  { key: 'facturesTotalementPayees', label: 'Totalement payees', icon: 'fa-check-double' },
  { key: 'paiementNonConfigure', label: 'Paiement non configure', icon: 'fa-sliders' },
  { key: 'bonsLivraisonNonFactures', label: 'BL non factures', icon: 'fa-truck-ramp-box' },
  { key: 'bonsCommandeSansLivraison', label: 'BC sans livraison', icon: 'fa-file-circle-question' }
];

const formatCurrency = (value) => new Intl.NumberFormat('fr-TN', {
  style: 'currency',
  currency: 'TND',
  minimumFractionDigits: 3
}).format(Number(value || 0));

const formatDate = (date) => date ? new Date(date).toLocaleDateString('fr-FR') : '-';

const FournisseurAchatsDashboard = ({
  getApiUrl,
  fournisseurs = [],
  onOpenFournisseur,
  onCreateFactureFromBl,
  onCreateBlFromBc
}) => {
  const [filters, setFilters] = useState({
    periode: 'month',
    dateDebut: '',
    dateFin: '',
    fournisseurId: '',
    statut: '',
    methode: ''
  });
  const [activeSection, setActiveSection] = useState('toutesFactures');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    return params.toString();
  }, [filters]);

  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.get(getApiUrl(`/factures-fournisseurs/dashboard/achats?${queryString}`), {
          headers: authHeaders()
        });
        setData(response.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Impossible de charger le dashboard achats fournisseurs.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [getApiUrl, queryString]);

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'periode' && value !== 'custom' ? { dateDebut: '', dateFin: '' } : {})
    }));
  };

  const stats = data?.stats || {};
  const sections = data?.sections || {};
  const currentRows = sections[activeSection] || [];
  const isEcheanceSection = activeSection.startsWith('echeances');
  const isNonConfiguredSection = activeSection === 'paiementNonConfigure';
  const isDocumentSection = activeSection === 'bonsLivraisonNonFactures' || activeSection === 'bonsCommandeSansLivraison';

  const statCards = [
    { label: 'Total achats fournisseurs', value: formatCurrency(stats.totalAchatsFournisseurs), icon: 'fa-truck-ramp-box', tone: 'blue' },
    { label: 'Total facture', value: formatCurrency(stats.totalFacture), icon: 'fa-file-invoice-dollar', tone: 'green' },
    { label: 'Total paye', value: formatCurrency(stats.totalPaye), icon: 'fa-money-bill-wave', tone: 'teal' },
    { label: 'Restant a payer', value: formatCurrency(stats.totalRestant), icon: 'fa-hourglass-half', tone: 'orange' },
    { label: 'Nombre factures', value: stats.nombreFactures || 0, icon: 'fa-file-lines', tone: 'slate' },
    { label: 'Nombre echeances', value: stats.nombreEcheances || 0, icon: 'fa-calendar-check', tone: 'slate' },
    { label: 'Echeances en retard', value: stats.echeancesEnRetard || 0, icon: 'fa-triangle-exclamation', tone: 'red' },
    { label: 'Echeances a venir', value: stats.echeancesAVenir || 0, icon: 'fa-clock', tone: 'blue' },
    { label: 'Echeances payees', value: stats.echeancesPayees || 0, icon: 'fa-circle-check', tone: 'green' },
    { label: 'Non configurees', value: stats.facturesEcheancesNonConfigurees || 0, icon: 'fa-sliders', tone: 'red' },
    { label: 'BL non factures', value: stats.bonsLivraisonNonFactures || 0, icon: 'fa-truck-ramp-box', tone: 'orange' },
    { label: 'BC sans livraison', value: stats.bonsCommandeSansLivraison || 0, icon: 'fa-file-circle-question', tone: 'red' }
  ];

  const openFacture = (row) => {
    const fournisseurId = row.fournisseur?.id;
    if (fournisseurId && onOpenFournisseur) {
      onOpenFournisseur(fournisseurId);
    }
  };

  const formatProducts = (produits = []) => {
    if (!Array.isArray(produits) || produits.length === 0) return '-';
    return produits
      .map(produit => `${produit.designation || 'Produit'}: ${produit.quantite || 0} ${produit.uniteMesure || ''}`.trim())
      .join(' | ');
  };

  const renderFactureRows = () => (
    <table className="fad-table">
      <thead>
        <tr>
          <th>Fournisseur</th>
          <th>Facture</th>
          <th>Date</th>
          <th>Methode</th>
          <th>TTC</th>
          <th>Paye</th>
          <th>Restant</th>
          <th>Statut</th>
          <th>Echeances</th>
          {isNonConfiguredSection && <th>Configuration manquante</th>}
          <th>Acces</th>
        </tr>
      </thead>
      <tbody>
        {currentRows.length === 0 ? (
          <tr><td colSpan={isNonConfiguredSection ? 11 : 10} className="fad-empty">Aucune facture trouvee pour ces filtres.</td></tr>
        ) : currentRows.map(row => (
          <tr key={row.id}>
            <td>{row.fournisseur?.nom || '-'}</td>
            <td>{row.numeroFacture || '-'}</td>
            <td>{formatDate(row.dateFacture)}</td>
            <td>{row.methodeLabel || '-'}</td>
            <td>{formatCurrency(row.montantTTC)}</td>
            <td>{formatCurrency(row.montantPaye)}</td>
            <td>{formatCurrency(row.montantRestant)}</td>
            <td><span className={`fad-status ${row.statutPaiement}`}>{row.statutPaiementLabel}</span></td>
            <td>{row.nombreEcheances || 0}</td>
            {isNonConfiguredSection && (
              <td className="fad-reasons">{(row.raisonsNonConfigure || []).join(' | ') || '-'}</td>
            )}
            <td>
              <button className="fad-icon-button" onClick={() => openFacture(row)} title="Acceder a la facture">
                <i className="fas fa-arrow-up-right-from-square"></i>
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderEcheanceRows = () => (
    <table className="fad-table">
      <thead>
        <tr>
          <th>Fournisseur</th>
          <th>Facture</th>
          <th>Montant echeance</th>
          <th>Montant restant</th>
          <th>Date echeance</th>
          <th>{activeSection === 'echeancesRetard' ? 'Jours retard' : 'Jours restants'}</th>
          <th>Methode</th>
          <th>Statut</th>
          <th>Acces</th>
        </tr>
      </thead>
      <tbody>
        {currentRows.length === 0 ? (
          <tr><td colSpan="9" className="fad-empty">Aucune echeance trouvee pour ces filtres.</td></tr>
        ) : currentRows.map(row => (
          <tr key={row.id}>
            <td>{row.fournisseur?.nom || '-'}</td>
            <td>{row.numeroFacture || '-'}</td>
            <td>{formatCurrency(row.montantEcheance)}</td>
            <td>{formatCurrency(row.montantRestant)}</td>
            <td>{formatDate(row.dateEcheance)}</td>
            <td>{activeSection === 'echeancesRetard' ? row.joursRetard : row.joursRestants}</td>
            <td>{row.methodeLabel || '-'}</td>
            <td><span className={`fad-status ${row.statut}`}>{row.statutLabel}</span></td>
            <td>
              <button className="fad-icon-button" onClick={() => openFacture(row)} title="Acceder a la facture">
                <i className="fas fa-arrow-up-right-from-square"></i>
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderDocumentRows = () => (
    <table className="fad-table">
      <thead>
        <tr>
          <th>{activeSection === 'bonsLivraisonNonFactures' ? 'BL' : 'BC'}</th>
          <th>Fournisseur</th>
          <th>Date</th>
          {activeSection === 'bonsLivraisonNonFactures' && <th>BC associe</th>}
          <th>Montant</th>
          <th>Produits / quantites</th>
          <th>Statut</th>
          <th>Jours ecoules</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {currentRows.length === 0 ? (
          <tr><td colSpan={activeSection === 'bonsLivraisonNonFactures' ? 9 : 8} className="fad-empty">Aucun document manquant trouve pour ces filtres.</td></tr>
        ) : currentRows.map(row => (
          <tr key={row.id}>
            <td>{row.numero || '-'}</td>
            <td>{row.fournisseur?.nom || '-'}</td>
            <td>{formatDate(row.dateDocument)}</td>
            {activeSection === 'bonsLivraisonNonFactures' && <td>{row.bonCommandeAssocie?.numero || '-'}</td>}
            <td>{formatCurrency(row.montantTTC || row.montant)}</td>
            <td className="fad-products">{formatProducts(row.produits)}</td>
            <td><span className={`fad-status ${row.statut}`}>{row.statut}</span></td>
            <td>{row.joursDepuisDocument || 0}</td>
            <td>
              <div className="fad-actions">
                <button className="fad-icon-button" onClick={() => openFacture(row)} title="Acceder au document">
                  <i className="fas fa-arrow-up-right-from-square"></i>
                </button>
                {activeSection === 'bonsLivraisonNonFactures' && (
                  <button className="fad-action-button" onClick={() => onCreateFactureFromBl?.(row)}>
                    Facturer
                  </button>
                )}
                {activeSection === 'bonsCommandeSansLivraison' && (
                  <button className="fad-action-button" onClick={() => onCreateBlFromBc?.(row)}>
                    Creer BL
                  </button>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="fad-dashboard">
      <div className="fad-toolbar">
        <div>
          <h2>Dashboard achats fournisseurs</h2>
          <p>Factures, paiements et echeances fournisseurs synchronises avec les donnees reelles.</p>
        </div>
        <span>{loading ? 'Chargement...' : `Mis a jour ${formatDate(data?.generatedAt)}`}</span>
      </div>

      <div className="fad-filters">
        <label>
          <span>Periode</span>
          <select value={filters.periode} onChange={(e) => handleFilterChange('periode', e.target.value)}>
            {PERIODS.map(period => <option key={period.value} value={period.value}>{period.label}</option>)}
          </select>
        </label>
        {filters.periode === 'custom' && (
          <>
            <label>
              <span>Debut</span>
              <input type="date" value={filters.dateDebut} onChange={(e) => handleFilterChange('dateDebut', e.target.value)} />
            </label>
            <label>
              <span>Fin</span>
              <input type="date" value={filters.dateFin} onChange={(e) => handleFilterChange('dateFin', e.target.value)} />
            </label>
          </>
        )}
        <label>
          <span>Fournisseur</span>
          <select value={filters.fournisseurId} onChange={(e) => handleFilterChange('fournisseurId', e.target.value)}>
            <option value="">Tous les fournisseurs</option>
            {fournisseurs.map(f => <option key={f._id} value={f._id}>{f.raisonSociale || f.nom}</option>)}
          </select>
        </label>
        <label>
          <span>Statut</span>
          <select value={filters.statut} onChange={(e) => handleFilterChange('statut', e.target.value)}>
            {STATUSES.map(status => <option key={status.value} value={status.value}>{status.label}</option>)}
          </select>
        </label>
        <label>
          <span>Methode</span>
          <select value={filters.methode} onChange={(e) => handleFilterChange('methode', e.target.value)}>
            {METHODS.map(method => <option key={method.value} value={method.value}>{method.label}</option>)}
          </select>
        </label>
      </div>

      {error && <div className="fad-error">{error}</div>}

      <div className="fad-stat-grid">
        {statCards.map(card => (
          <div className="fad-stat-card" key={card.label}>
            <div className={`fad-stat-icon ${card.tone}`}><i className={`fas ${card.icon}`}></i></div>
            <div>
              <strong>{card.value}</strong>
              <span>{card.label}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="fad-methods">
        <h3>Repartition des montants par methode de paiement</h3>
        <div className="fad-method-grid">
          {(data?.repartitionMethodes || []).map(row => (
            <div className="fad-method-card" key={row.methode}>
              <div>
                <strong>{row.methodeLabel}</strong>
                <span>{row.nombreEcheances} echeance(s)</span>
              </div>
              <p>Paye: {formatCurrency(row.montantPaye)}</p>
              <p>Restant / a venir: {formatCurrency(row.montantRestant)}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="fad-sections">
        {SECTIONS.map(section => (
          <button
            key={section.key}
            className={activeSection === section.key ? 'active' : ''}
            onClick={() => setActiveSection(section.key)}
          >
            <i className={`fas ${section.icon}`}></i>
            {section.label}
          </button>
        ))}
      </div>

      <div className="fad-table-wrap">
        <div className="fad-table-title">
          <h3>{SECTIONS.find(section => section.key === activeSection)?.label}</h3>
          <span>{currentRows.length} element(s)</span>
        </div>
        {activeSection === 'paiementNonConfigure' && currentRows.length > 0 && (
          <div className="fad-warning">
            Ces factures ont une methode, un echeancier ou un total d'echeances incomplet.
          </div>
        )}
        {isDocumentSection ? renderDocumentRows() : isEcheanceSection ? renderEcheanceRows() : renderFactureRows()}
      </div>
    </div>
  );
};

export default FournisseurAchatsDashboard;
